import { useAuth } from '@/hooks/use-auth';
import { AuthService } from '@/services/auth.service';
import { CompanyService, resolveActiveCompanyPayload } from '@/services/company.service';
import type { Company } from '@/types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryKey } from '@tanstack/react-query';
import { useState } from 'react';
import { CompanyContext } from './CompanyContext';

const COMPANY_SCOPED_QUERY_KEYS: QueryKey[] = [
  ['auth', 'profile'],
  ['auth', 'companies'],
  ['companies', 'my'],
  ['services'],
  ['bookings'],
  ['companies', 'professionals'],
  ['companies', 'working-hours'],
  ['dashboard'],
  ['notifications'],
  ['users', 'me', 'notification-preferences'],
  ['rbac'],
];

export function CompanyProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useAuth();
  const [activeCompanyId, setActiveCompanyId] = useState<string | null>(null);

  const { data: companiesData, isLoading: isCompaniesLoading } = useQuery({
    queryKey: ['auth', 'companies'],
    queryFn: AuthService.companies,
    enabled: isAuthenticated,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const { data: activeCompanyData, isLoading: isActiveCompanyLoading } = useQuery({
    queryKey: ['companies', 'my'],
    queryFn: async (): Promise<Company | null> => {
      const response = await CompanyService.getMyCompany();
      if (response.status === 401 || response.status === 404) {
        return null;
      }
      return resolveActiveCompanyPayload(response.data);
    },
    enabled: isAuthenticated,
    retry: false,
    staleTime: 1000 * 60 * 5,
  });

  const userCompanies = companiesData ?? [];

  const cookieCompanyId = (() => {
    const id = activeCompanyData?.id;
    if (!id) return null;
    const exists = userCompanies.some((item) => item.company.id === id);
    return exists ? id : null;
  })();

  const selectedCompanyId = (() => {
    if (activeCompanyId) {
      const exists = userCompanies.some((item) => item.company.id === activeCompanyId);
      if (exists) return activeCompanyId;
    }

    return cookieCompanyId;
  })();

  const activeCompany = (() => {
    if (!selectedCompanyId) return null;
    const selectedCompany = userCompanies.find((item) => item.company.id === selectedCompanyId)?.company ?? null;
    const hydratedActiveCompany = activeCompanyData;

    if (hydratedActiveCompany?.id === selectedCompanyId) {
      return {
        ...(selectedCompany ?? {}),
        ...hydratedActiveCompany,
      };
    }

    return selectedCompany;
  })();

  const setActiveCompany = async (company: Company) => {
    await AuthService.setActiveCompany(company.id);
    setActiveCompanyId(company.id);
    queryClient.setQueryData(['companies', 'my'], company);

    await Promise.all(COMPANY_SCOPED_QUERY_KEYS.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
    await Promise.all(
      COMPANY_SCOPED_QUERY_KEYS.map((queryKey) => queryClient.refetchQueries({ queryKey, type: 'active' })),
    );
  };

  const clearActiveCompany = () => {
    setActiveCompanyId(null);
    queryClient.setQueryData(['companies', 'my'], null);
  };

  const refreshCompanies = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['auth', 'companies'] }),
      queryClient.invalidateQueries({ queryKey: ['companies', 'my'] }),
      queryClient.refetchQueries({ queryKey: ['auth', 'companies'], type: 'active' }),
      queryClient.refetchQueries({ queryKey: ['companies', 'my'], type: 'active' }),
    ]);
  };

  const value = {
    userCompanies,
    activeCompany,
    isLoading: isCompaniesLoading || isActiveCompanyLoading,
    setActiveCompany,
    refreshCompanies,
    clearActiveCompany,
  };

  return <CompanyContext.Provider value={value}>{children}</CompanyContext.Provider>;
}
